<template>
  <v-dialog
    v-model="dialog"
    scrollable
    persistent
    transition="dialog-bottom-transition"
    width="50%"
    @click:outside="handleClose"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        class="ml-2 white--text"
        color="primary"
        :loading="loading"
        :disabled="disabled"
        v-on="on"
        @click="discover"
      >
        <v-icon
          small
          left
        >
          mdi-cloud-search-outline
        </v-icon>
        <span>Discover</span>
      </v-btn>
    </template>
    <v-card class="pa-0">
      <v-toolbar
        flat
      >
        <v-toolbar-title>
          Datasets discovery
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn
            icon
            @click="handleClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text id="select-table-columns-list">
        <template v-if="loading">
          <div class="text-center mt-5 mb-3">
            <v-icon
              size="65"
              class="pb-2"
            >
              mdi-cloud-search-outline
            </v-icon>

            <div class="display-1 font-weight-light">
              Discovering new tables
            </div>
            <p class="caption">
              We are discovering any available tables in your configuration. Please wait....
            </p>
          </div>
        </template>
        <template v-else-if="discovery_errors.length>0">
          <div class="text-center mt-5 mb-3 ">
            <v-icon
              size="65"
              class="pb-2"
            >
              mdi-cloud-alert
            </v-icon>

            <div class="display-1 font-weight-light">
              Connection attempt failed
            </div>
            <v-alert
              dense
              outlined
              type="error"
              icon="mdi-cloud-alert"
              class="text-left mt-5"
            >
              <div
                v-for="(e,index) in discovery_errors"
                :key="index"
                class="body-1"
              >
                {{ e }}
              </div>
            </v-alert>
          </div>
        </template>
        <template v-else>
          <v-row>
            <v-col>
              <v-list>
                <v-subheader>Let Himydata know what data you want in your warehouse.</v-subheader>
                <v-col
                  v-for="(table, index) in tables"
                  :ref="'table_columns_'+table.name"
                  :key="index"
                  class="xs"
                  style="padding:5px 15px;"
                >
                  <v-card>
                    <v-container>
                      <div
                        role="table-sync"
                        class="mt-3"
                      >
                        <div
                          style="display: flow-root;cursor:pointer;"
                          @click="discoverTableFields(table,index)"
                        >
                          <div role="title-label">
                            Table
                          </div>
                          <span role="title">{{ table.name }}</span>
                          <div
                            style="float:right;"
                            :class="(table.sync ? 'active' : '') + ' sync'"
                          >
                            <v-btn
                              text
                              small
                              :loading="table.syncLoading"
                            >
                              <v-icon role="icon">
                                {{ table.sync ? 'mdi-sync' : 'mdi-sync-off' }}
                              </v-icon>
                              {{ table.sync ? 'Synchronized' : 'Not Synchronized' }}
                            </v-btn>
                          </div>
                        </div>
                        <template>
                          <v-expand-transition>
                            <v-col
                              v-show="table.sync"
                              cols="12"
                              class="dropdown text-center"
                            >
                              <span
                                style="cursor:pointer;"
                                @click="table.showColumns=!table.showColumns"
                              >
                                <v-icon>{{ table.showColumns ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                Manage selected columns
                              </span>
                            </v-col>
                          </v-expand-transition>
                          <v-expand-transition>
                            <div
                              v-if="table.showColumns && table.sync"
                              role="columns-table"
                            >
                              <v-row
                                justify="center"
                              >
                                <v-col
                                  cols="12"
                                  sm="2">
                                <v-text-field
                                  v-model="table.delta_minute"
                                  label="Minute before synchronization"
                                  placeholder="Minute before synchronization"
                                />
                                </v-col>
                                <v-col
                                  cols="12"
                                  sm="5">
                                <v-text-field
                                  v-model="table.prefix"
                                  label="Prefix"
                                  placeholder="(can be empty)"
                                />
                                </v-col>
                                <v-col
                                  cols="12"
                                  sm="5">
                                <v-text-field
                                  v-model="table.suffix"
                                  label="Suffix"
                                  placeholder="(can be empty)"
                                />
                                </v-col>
                              </v-row>

                              <v-data-table
                                class="scrollBody"
                                :items-per-page="-1"
                                hide-default-footer
                                :headers="[ { text: 'Name', value: 'name' }, { text: 'Type', value: 'type' },{ text: 'Key', value: 'primary_key' }, { text: 'Replication Key', value: 'replication_key' } ]"
                                :items="table.columns"
                                item-key="name"
                                show-select
                                @item-selected="(e)=>{e.item['selected']=e.value}"
                              >
                                <template v-slot:header.data-table-select="o">
                                  <v-checkbox
                                    v-model="table.allColumnsSelected"
                                    @change="e=>handleSelectAll(e,table)"
                                  />
                                </template>
                                <template v-slot:item.data-table-select="o">
                                  <v-checkbox
                                    v-model="o.item.selected"
                                    :disabled="o.item.primary_key"
                                    @change="e=>handleSelectColumn(e,table,o)"
                                  />
                                </template>
                                <template v-slot:item.primary_key="o">
                                  <template v-if="o.value">
                                    <v-icon
                                      small
                                      left
                                    >
                                      mdi-key-outline
                                    </v-icon> Primary key
                                  </template>
                                </template>
                                <template v-slot:item.replication_key="o">
                                  <v-checkbox
                                    v-model="o.item.replication_key"
                                    :disabled="!o.item.selected"
                                    @change="e=>handleSelectReplicationKey(o.item,table)"
                                  />
                                </template>
                              </v-data-table>
                            </div>
                          </v-expand-transition>
                        </template>
                      </div>
                    </v-container>
                  </v-card>
                </v-col>
              </v-list>
            </v-col>
            <v-divider vertical />
          </v-row>
        </template>
      </v-card-text>
      <v-divider />
      <v-card-actions class="text-right">
        <v-col
          cols="12"
          class="d-inline-flex justify-center pt-2 pb-2"
        >
          <v-btn
            class="mx-2"
            text
            @click="handleClose"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            outlined
            :disabled="tables.filter(o => { return o.sync }).length <= 0 || loading"
            :loading="loading"
            @click="handleClickCall"
          >
            <v-icon
              left
              small
            >
              mdi-database-import
            </v-icon>
            Import Datasets
          </v-btn>
        </v-col>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
  import SingerService from './services/SingerService'
  import axios from 'axios'
  export default {
    props: {
      config: {
        type: Number,
        default: null,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data () {
      return {
        dialog: false,
        loading: false,
        discovery_errors: [],
        tables: [],
        streams: [],
        cancelToken: null,
      }
    },
    computed: {
      innerHeight: function () {
        return window.innerHeight
      },
    },
    methods: {
      handleReset () {
        this.loading = false
        this.tables = []
        this.streams = []
      },
      handleClose () {
        let self = this
        this.dialog = false
        this.cancelToken()
        _.delay(() => {
          self.loading = false
          self.tables = []
          self.streams = []
        }, 500)
      },
      discover () {
        let self = this
        this.loading = true
        let cancelToken = new axios.CancelToken(function (c) { self.cancelToken = c })
        SingerService.discover(self.config, { cancelToken: cancelToken })
          .then(function (response) {
            if (response.data.length) {
              self.streams = response.data
              self.tables = _.map(response.data, (o, i) => {
                return {
                  name: o,
                  syncLoading: false,
                  sync: false,
                  showColumns: false,
                  columns: [],
                  allColumnsSelected: true,
                  delta_minute: 1,
                  replication: {"method" : "FULL_TABLE", "key": null},
                  prefix: null,
                  suffix: null,
                }
              })
            }
          })
          .catch(function (error) {
            self.discovery_errors = error.response.data.error
            if (!cancelToken.reason) {
              self.$store.commit('push', { msg: _.join(error.response.data.error, '\n'), color: 'error' })
            }
          }).finally(() => {
            self.loading = false
          })
      },
      discoverTableFields (table, index) {
        if (table.sync) {
          table.sync = !table.sync

          return
        }
        let self = this
        table.syncLoading = true
        SingerService.discoverFields(self.config, table.name)
          .then(function (response) {
            table.columns = _.map(response.data, (o, i) => {
              o.selected = true
              return o
            })
            table.sync = !table.sync
            if (table.sync) {
              for (let i = 0; i < self.tables.length; i++) { self.tables[i].showColumns = index === i }
            }
            setTimeout(() => {
              let el = 'table_columns_' + table.name
              if (self.$refs[el]) {
                self.$vuetify.goTo(self.$refs[el][0], {
                  duration: 500,
                  offset: 100,
                  easing: 'easeInOutCubic',
                  container: '#select-table-columns-list',
                })
              }
            }, 500)
          })
          .catch(() => {
            self.$store.commit('push', { msg: 'Error retrieving ' + table.name + ' fields', color: 'error' })
          }).finally(() => {
            table.syncLoading = false
          })
      },
      handleSelectColumn: function (e, table, column) {
        _.map(table.columns, (o)=>{
          if(!o.selected && o.replication_key){
            o.replication_key = false
            table.replication["method"] = "FULL_TABLE"
            table.replication["key"] = null
          }
        })
        table.allColumnsSelected = table.columns.filter(o => { return !o.selected }) <= 0
      },
      handleSelectAll: function (e, table) {
        if (e) {
          _.map(table.columns, (o, i) => {
            o.selected = true
          })
        } else {
          _.map(table.columns, (o, i) => {
            o.selected = !!o.primary_key
          })
        }
      },
      handleSelectReplicationKey: function (e, table) {
        if (e.replication_key) {
          table.replication["method"] = "INCREMENTAL"
          table.replication["key"] = e.name
          _.map(table.columns, (o, i) => {
            if (o !== e){
              o.replication_key = false
            }
          })
        }
        else{
          table.replication["method"] = "FULL_TABLE"
          table.replication["key"] = null
        }
      },
      handleClickPreviousSingerStreams: function () {
        this.tables = []
      },
      handleClickCall: async function () {
        let self = this
        let promises = []
        let tables = _.filter(self.tables, o => {
          return (
            o.sync &&
            _.filter(o.columns, k => {
              return k.selected
            }).length > 0
          )
        })
        if (tables.length <= 0) {
          self.$store.commit('push', {
            msg: 'You have not synchronized any table yet',
            color: 'error',
          })
          return
        }
        let streams = {}

        _.map(tables, table => {
          let fields = _.map(
            _.filter(table.columns, k => {
              return k.selected
            }),
            'name'
          )
          streams[table.name] = {"fields": fields,
                                "replication_method": table.replication["method"],
                                "replication_key": table.replication["key"],
                                "minutes_delta_next_update":table.delta_minute,
                                "prefix": table.prefix,
                                "suffix": table.suffix,
                                }
        })
        SingerService.call(self.config, streams).then(function () {
          self.$store.commit('push', {
            msg: ' Connector updated',
            color: 'success',
          })
        }).catch(err => {
          self.$store.commit('push', { msg: 'Erro' + err })
        }).finally(() => {
          self.dialog = false
        })
      },

    },
  }
</script>
<style>
.integration-description {
  overflow: hidden;
  height: 40px;
  text-overflow: ellipsis;
}
.create-config-btn {
  position: absolute;
  top: 70px;
  right: 20px;
}
[role="table-sync"] .columns .dropdown > span > i {
  margin: -4px 2px;
}
[role="table-sync"] .columns .dropdown {
  text-align: center;
}
[role="table-sync"] .columns .dropdown > span {
  display: inline-block;
  margin: 7px 0px 0px;
  font-size: 13px;
  color: #c7c7c7;
}
[role="table-sync"] .columns [role="columns-table"] {
  margin-top: 15px;
  padding-top: 20px;
}
[role="table-sync"] [role="title-label"] {
  font-size: 12px;
  color: lightgray;
  position: absolute;
  top: 6px;
}
[role="table-sync"] [role="title"] {
  font-size: 20px;
}
[role="table-sync"] .sync {
  margin-top: -8px;
}
[role="table-sync"] .sync .v-btn__content {
  color: lightgray;
}
[role="table-sync"] .active.sync .v-btn__content {
  color: #1fcc95;
}
[role="table-sync"] .sync [role="icon"] {
  font-size: 17px;
  margin: 0 5px 0px 0px;
}
.scrollBody table tbody {
  display: block;
  max-height: 280px;
  overflow: auto;
}
.scrollBody table thead {
  text-align: left;
}
.scrollBody table thead,
.scrollBody table tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
.scrollBody table thead th:first-child {
  width: 50px;
}
.scrollBody table tbody tr td:first-child {
  width: 50px;
}
.config-name-field {
  padding: 10px 15px 0;
}
.global-checkbox {
  padding: 0 0 0 15px;
}
.global-tooltip {
  margin-top: -8px;
  margin-left: 5px;
}
.select-config {
  padding: 0 15px;
}
</style>
